import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import * as monthUtils from '@actual-app/core/shared/months';

import { puedeAbrirse } from '#components/mobile/budget/recuerdos';
import type { Recuerdo } from '#components/mobile/budget/recuerdos';
import {
  useAccionesRecuerdo,
  useDetectorLlegadas,
  useRecuerdos,
} from '#components/mobile/budget/useRecuerdos';
import type { EstadoTravesiaCategoria } from '#components/mobile/budget/useRecuerdos';
import { useCerrarConAtras } from '#components/mobile/ui/atras';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useNavigate } from '#hooks/useNavigate';

import { llegadaVistaLocal, marcarVistaLocal } from './escenaLlegada';
import { LlegadaBotella } from './LlegadaBotella';

/** Ruta de «Tu bitácora». */
export const RUTA_BITACORA = '/bitacora';

export type ContextoLlegadas = {
  /** Travesías del mes por categoría (las que tienen meta con fecha). */
  travesias: ReadonlyMap<string, EstadoTravesiaCategoria>;
  /** Botellas llegadas que este dispositivo aún no ha abierto. */
  pendientes: readonly Recuerdo[];
  /** Categorías ocultas, de grupo oculto o borradas: sin travesía visible. */
  ocultas: ReadonlySet<string>;
  /** Abre la llegada de una botella (siempre tras un toque). */
  abrir: (rid: string) => void;
};

const SIN_LLEGADAS: ContextoLlegadas = {
  travesias: new Map(),
  pendientes: [],
  ocultas: new Set(),
  abrir: () => undefined,
};

const Contexto = createContext<ContextoLlegadas>(SIN_LLEGADAS);

/** Travesías y llegadas pendientes (vacío fuera del proveedor). */
export function useLlegadas(): ContextoLlegadas {
  return useContext(Contexto);
}

/**
 * Monta el detector de llegadas una sola vez para toda la app móvil y pinta
 * la llegada a pantalla completa cuando se abre una botella (desde el aviso
 * de Inicio o desde la ficha de la categoría). En «Solo ver» no escribe
 * nada: una marca local evita repetir la llegada en este dispositivo.
 */
export function ProveedorLlegadas({ children }: { children: ReactNode }) {
  const { isNarrowWidth } = useResponsive();
  const detector = useDetectorLlegadas(isNarrowWidth);
  const recuerdos = useRecuerdos();
  const acciones = useAccionesRecuerdo();
  const [soloLectura] = useMetadataPref('rumboSoloLectura');
  const navigate = useNavigate();
  const [abierta, setAbierta] = useState<string | null>(null);
  // Vistas en «Solo ver» durante esta sesión (además de la marca local).
  const [vistas, setVistas] = useState<ReadonlySet<string>>(new Set());
  const hoy = monthUtils.currentDay();

  const pendientes = detector.pendientes.filter(
    r =>
      puedeAbrirse(r, hoy) &&
      !(
        soloLectura &&
        (vistas.has(r.base.rid) || llegadaVistaLocal(r.base.rid))
      ),
  );

  const recuerdo = abierta
    ? (recuerdos.find(r => r.base.rid === abierta) ?? null)
    : null;

  const cerrar = () => setAbierta(null);
  // «Atrás» del móvil cierra la botella (antes cambiaba de pantalla y la
  // botella se quedaba encima).
  const cerrarYLuego = useCerrarConAtras(abierta != null, cerrar);

  const valor: ContextoLlegadas = {
    travesias: detector.travesias,
    pendientes,
    ocultas: detector.ocultas,
    abrir: rid => {
      if (soloLectura) {
        marcarVistaLocal(rid);
        setVistas(prev => new Set(prev).add(rid));
      }
      setAbierta(rid);
    },
  };

  return (
    <Contexto.Provider value={valor}>
      {children}
      {recuerdo?.llegada && (
        <LlegadaBotella
          key={recuerdo.base.rid}
          recuerdo={recuerdo}
          disp={acciones.disp}
          soloLectura={!acciones.puedeEscribir}
          conGesto
          onGuardar={() => acciones.marcarAbierta(recuerdo.base.rid)}
          onResponder={texto =>
            acciones.responderLlegada(recuerdo.base.rid, texto)
          }
          onVerBitacora={() =>
            // El cuaderno se abre en la página recién sellada.
            cerrarYLuego(
              () =>
                void navigate(
                  `${RUTA_BITACORA}?rid=${encodeURIComponent(recuerdo.base.rid)}`,
                ),
            )
          }
          onCerrar={() => cerrarYLuego()}
        />
      )}
    </Contexto.Provider>
  );
}
